import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  Download,
  FileText,
  Hash,
  Printer,
  Warehouse,
  X,
} from 'lucide-react';
import {
  DEFAULT_FORM_LOCALE,
  DOC_LOCALES,
  MIN_REJECT_REASON_LENGTH,
  isValidRejectReason,
  type DocLocale,
  type SurveyDto,
} from '@eombor/contracts';
import { Badge, Button, Dialog, Input, Skeleton, Table, cn, type Column } from '@eombor/ui';
import { downloadBlob } from '../../../shared/api/download.js';
import { printBlob } from '../../../shared/api/print.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { requisitionApi } from '../../requisition/api/requisition.api.js';
import { useSetSurveyFormNo, useSurvey } from '../model/accounting.queries.js';

/**
 * BUXGALTERNING KO'RIB CHIQISH OYNASI.
 *
 * Ilgari buxgalterda ikki ayri narsa bor edi: kartochkada tugmalar,
 * alohida oynada esa blankning PDF ko'rinishi. Qaror qabul qilish uchun
 * ikkalasi BIR VAQTDA kerak — qog'ozni ko'rmasdan tasdiqlash, yoki
 * qog'ozni ko'rib bo'lib uni yopib, tugmani boshqa joydan izlash.
 *
 * Endi hammasi bitta joyda: chapda qog'ozning O'ZI, o'ngda hujjat
 * rekvizitlari va satrlar, pastda qaror.
 *
 * BLANKDAGI RAQAM ham shu yerda yoziladi. `docNo` — tizimning ichki
 * raqami: u so'rovnoma tug'ilganda, skladchining tranzaksiyasida beriladi
 * va o'sha paytda buxgalter hujjatni hali ko'rmagan ham. Qog'ozdagi
 * «ТРЕБОВАНИЕ №» esa uning O'Z daftaridagi raqam va u tizimning sanog'iga
 * bo'ysunmaydi — ilgari blankka ichki raqam bosilardi va buxgalter uni
 * qo'lda o'chirib, ustiga o'zinikini yozardi.
 */

const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** "27.08.2026 16:05" */
const formatMoment = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

interface ReviewLine {
  id: string;
  lineNo: number;
  nomenclatureId: string;
  qty: number;
}

export interface SurveyReviewDialogProps {
  open: boolean;
  survey: SurveyDto;
  onClose: () => void;
  /** Nomenklatura nomi/kodi — sahifada allaqachon yuklangan ma'lumotnoma. */
  nameOf: (nomenclatureId: string) => { name: string; code: string; unit?: string } | undefined;
  /** Ombor nomi identifikator o'rniga. */
  warehouseName?: string;
  canApprove?: boolean;
  canReject?: boolean;
  /**
   * Blankdagi raqamni yoza oladimi.
   *
   * Odatda `canApprove` bilan bir xil: qog'ozni imzolaydigan odam uning
   * raqamini ham qo'yadi (server ham shu qoidani tekshiradi). Skladchi
   * oynani KO'RISH uchun ochadi va unga tahrir maydonini ko'rsatish
   * bosilgan zahoti 403 beradigan tugmani taklif qilish bo'lardi.
   */
  canSetFormNo?: boolean;
  pending?: boolean;
  error?: string | null;
  onApprove?: () => void;
  onReject?: (reason: string) => void;
}

export const SurveyReviewDialog = ({
  open,
  survey,
  onClose,
  nameOf,
  warehouseName,
  canApprove = false,
  canReject = false,
  canSetFormNo = canApprove,
  pending = false,
  error = null,
  onApprove,
  onReject,
}: SurveyReviewDialogProps) => {
  const [locale, setLocale] = useState<DocLocale>(DEFAULT_FORM_LOCALE);
  const [formNo, setFormNo] = useState('');
  /*
   * RAD ETISH sababi — oynaning ICHIDA.
   *
   * Alohida oyna ochilsa, qog'oz ko'zdan g'oyib bo'lardi va sabab
   * yozayotgan odam nimani rad etayotganini ko'rmay qolardi. Sababni esa
   * so'rovchi o'qiydi: u aynan qog'ozdagi narsaga tegishli bo'lishi kerak.
   */
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const detail = useSurvey(open ? survey.id : null);
  const saveFormNo = useSetSurveyFormNo(survey.id);
  /* Raqam faqat OCHIQ hujjatda va faqat imzolaydigan odam tomonidan
     o'zgaradi — ikkala shart ham serverda takrorlanadi. */
  const locked = survey.status !== 'PENDING' || !canSetFormNo;
  const decided = survey.status !== 'PENDING';

  /* Maydon SAQLANGAN qiymatdan boshlanadi. Bo'sh bo'lsa bo'sh qoladi —
     ichki raqamni oldindan yozib qo'yish "men shuni tanladim" degan
     yolg'on taassurot qoldirardi va buxgalter uni o'chirib yozishga
     majbur bo'lardi. */
  useEffect(() => {
    if (open) setFormNo(survey.formNo ?? '');
  }, [open, survey.formNo]);

  /* Har ochilishda rad etish holati tozalanadi: yarim yozilgan sabab
     keyingi hujjatga o'tib qolsa, u boshqa hujjat haqida bo'lardi. */
  useEffect(() => {
    if (!open) return;
    setRejecting(false);
    setReason('');
  }, [open]);

  /*
   * Blank — PDF ning O'ZI, uning brauzerdagi qayta chizilgan nusxasi emas:
   * buxgalter ko'rgan narsa printerdan chiqadigan narsa bilan bir xil
   * bo'lishi kerak.
   *
   * MANBA — TALABNOMA, so'rovnoma emas. `surveys/:id/requirement.pdf` ham
   * xuddi shu blankni chizadi, lekin uning IMZO TASMASI («Отпустил» /
   * «Получил») HECH QACHON to'lmaydi: kim bergani va kim qabul qilgani
   * talabnoma O'TISHLARIDA yozilgan va so'rovnomada bunday maydon umuman
   * yo'q. Buxgalter tasdiqlangan hujjatni qayta ochganda aynan shu sabab
   * ikkala katak bo'sh chiqardi.
   *
   * Ruxsat: bu yo'l `requisition.read` va uning DOIRASINI talab qiladi.
   * Oynani ochadigan ikkala rolda ham u bor (`buxgalter`, `skladchi`) —
   * so'rovnomani o'qish ruxsatlari ro'yxati `accounting.service.ts` dagi
   * `SURVEY_READ_PERMISSIONS` da. Yo'q bo'lsa blank o'rniga xato matni
   * chiqadi, jimgina imzosiz qog'oz EMAS.
   *
   * Kalitda `survey.formNo` turadi: raqam saqlangach blank QAYTA
   * so'ralishi kerak, aks holda ekranda eski raqamli qog'oz qolardi.
   */
  const blank = useQuery({
    queryKey: ['accounting', 'requirement', 'requisition', survey.requisitionId, locale, survey.formNo],
    queryFn: () => requisitionApi.requirementPdf(survey.requisitionId, locale),
    enabled: open,
    retry: false,
  });

  /* `blob:` manzili oyna yopilganda BO'SHATILADI: aks holda har ochilishda
     brauzer xotirasida bitta PDF qolib ketardi. */
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const file = blank.data;
    if (file === undefined) return;
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
      setUrl(null);
    };
  }, [blank.data]);

  /* Blankka TIZIM raqami tushmaydi — u buxgalterning daftariga
     tushmagan begona raqam bo'lardi. To'ldirilmaguncha o'rni bo'sh. */
  const printedNo = survey.formNo;
  const fileName = `${printedNo ?? survey.docNo}-trebovaniya.pdf`;
  const dirty = formNo.trim() !== (survey.formNo ?? '');
  /* RAQAMSIZ tasdiq bo'lmaydi: imzolangan qog'ozda raqam bo'lishi shart
     va mahsulot keyinchalik aynan shu raqam bo'yicha beriladi. Server
     ham shu qoidani tekshiradi (409). */
  const missingNo = survey.formNo === null;

  const columns: Column<ReviewLine>[] = [
    { key: 'lineNo', header: '№', mono: true, width: '3rem' },
    {
      key: 'nomenclatureId',
      header: t('accounting.colNomenclature'),
      render: (line) => {
        const info = nameOf(line.nomenclatureId);
        return (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm text-text">
              {info?.name ?? `#${line.nomenclatureId}`}
            </span>
            {info?.code === undefined ? null : (
              <span className="tabular text-xs text-text-faint">{info.code}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'qty',
      header: t('accounting.colQty'),
      align: 'right',
      mono: true,
      width: '7rem',
      render: (line) => qtyText(line.qty),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('accounting.reviewTitle', { docNo: printedNo ?? survey.docNo })}
      size="lg"
      className="max-w-6xl"
      footer={
        <>
          {error === null ? null : (
            <span role="alert" className="mr-auto text-sm text-danger">
              {error}
            </span>
          )}
          <Button variant="ghost" onClick={onClose}>
            {t('accounting.close')}
          </Button>
          <Button
            variant="secondary"
            disabled={blank.data === undefined}
            onClick={() => {
              if (blank.data !== undefined) downloadBlob(blank.data, fileName);
            }}
          >
            <Download size={14} aria-hidden />
            {t('accounting.downloadPdf')}
          </Button>
          <Button
            variant="secondary"
            disabled={blank.data === undefined}
            onClick={() => {
              if (blank.data !== undefined) printBlob(blank.data);
            }}
          >
            <Printer size={14} aria-hidden />
            {t('accounting.requirementPrint')}
          </Button>
          {rejecting ? (
            <>
              <Button variant="ghost" onClick={() => setRejecting(false)}>
                {t('requisition.cancel')}
              </Button>
              <Button
                variant="danger"
                loading={pending}
                disabled={!isValidRejectReason(reason)}
                onClick={() => onReject?.(reason.trim())}
              >
                <X size={14} aria-hidden />
                {t('accounting.reject')}
              </Button>
            </>
          ) : (
            <>
              {canReject && onReject !== undefined ? (
                <Button variant="danger" disabled={pending} onClick={() => setRejecting(true)}>
                  <X size={14} aria-hidden />
                  {t('accounting.reject')}
                </Button>
              ) : null}
              {canApprove && onApprove !== undefined ? (
                <Button
                  loading={pending}
                  /* Tugma o'chiq turgani NEGA ekani yuqorida yozilgan:
                     raqam maydonining ostida qizil eslatma turadi. */
                  disabled={missingNo}
                  onClick={onApprove}
                >
                  <Check size={14} aria-hidden />
                  {t('accounting.approve')}
                </Button>
              ) : null}
            </>
          )}
        </>
      }
    >
      {/* Sabab maydoni qog'ozning USTIDA emas, YONIDA ochiladi: rad etish
          sababini yozayotgan odam nimani rad etayotganini ko'rib turishi
          kerak. */}
      {!rejecting ? null : (
        <div
          className={cn(
            'mb-4 flex flex-col gap-2 rounded-[var(--radius-md)] border',
            'border-danger/25 bg-danger-soft p-3',
          )}
        >
          <Input
            autoFocus
            label={t('requisition.rejectLabel')}
            hint={t('requisition.rejectHint')}
            autoComplete="off"
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={
              reason.trim() === '' || isValidRejectReason(reason)
                ? undefined
                : t('requisition.rejectTooShort', { min: MIN_REJECT_REASON_LENGTH })
            }
          />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        {/* ── Chap: QOG'OZNING O'ZI ─────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-text-faint uppercase">
              <FileText size={12} aria-hidden />
              {t('accounting.reviewBlank')}
            </span>
            <Select
              hiddenLabel
              label={t('accounting.requirementLocale')}
              className="w-[10rem]"
              value={locale}
              options={DOC_LOCALES.map((code) => ({
                value: code,
                label: t(`accounting.requirementLocale.${code}`),
              }))}
              onChange={(event) => setLocale(event.target.value as DocLocale)}
            />
          </div>

          {blank.isError ? (
            <p role="alert" className="text-sm text-danger">
              {t('accounting.requirementFailed')}
            </p>
          ) : url === null ? (
            <Skeleton className="h-[30rem] w-full" />
          ) : (
            /* Balandlik QAT'IY: `iframe` o'z mazmuniga qarab o'smaydi va
               balandliksiz u nol piksel bo'lib qolardi. */
            <iframe
              src={url}
              title={t('accounting.reviewTitle', { docNo: printedNo ?? survey.docNo })}
              className="h-[30rem] w-full rounded-[var(--radius-sm)] border border-border bg-surface-2"
            />
          )}
        </div>

        {/* ── O'ng: RAQAM, rekvizitlar va satrlar ───────────────── */}
        <div className="flex min-w-0 flex-col gap-4">
          {/*
            BLANKDAGI RAQAM — oynadagi eng tepada va eng katta maydon.
            Buxgalter qog'ozni ko'zdan kechirib, birinchi navbatda o'z
            daftaridagi raqamni qo'yadi; qolgani ko'rib chiqish.
          */}
          <div
            className={cn(
              'flex flex-col gap-2 rounded-[var(--radius-md)] border',
              'border-[var(--color-accent)]/25 bg-accent-soft p-3',
            )}
          >
            <span className="flex items-center gap-1.5 text-xs font-semibold text-accent">
              <Hash size={13} aria-hidden />
              {t('accounting.formNoTitle')}
            </span>

            <div className="flex items-end gap-2">
              {/* Yorliq YUQORIDA turibdi (bo'lim sarlavhasi) — maydonda
                  takrorlansa, bitta so'z ikki marta yozilgan bo'lardi.
                  Ekran o'quvchi uchun `aria-label` qoladi. */}
              <Input
                mono
                className="flex-1"
                aria-label={t('accounting.formNoTitle')}
                placeholder={survey.docNo}
                autoComplete="off"
                maxLength={60}
                disabled={locked}
                value={formNo}
                onChange={(event) => setFormNo(event.target.value)}
              />
              <Button
                variant="secondary"
                loading={saveFormNo.isPending}
                disabled={locked || !dirty}
                onClick={() =>
                  saveFormNo.mutate(formNo.trim() === '' ? null : formNo.trim())
                }
              >
                {t('accounting.formNoSave')}
              </Button>
            </div>

            <p className="text-xs leading-5 text-text-muted">
              {decided
                ? t('accounting.formNoLocked')
                : canSetFormNo
                  ? t('accounting.formNoHint')
                  : t('accounting.formNoReadonly', { docNo: printedNo ?? '—' })}
            </p>

            {/* Raqam MAJBURIY — qog'oz raqamsiz imzolanmaydi. */}
            {missingNo && canSetFormNo && !decided ? (
              <p className="text-xs leading-5 font-medium text-danger">
                {t('accounting.formNoRequired')}
              </p>
            ) : null}

            {saveFormNo.isError ? (
              <p role="alert" className="text-xs text-danger">
                {t('accounting.formNoFailed')}
              </p>
            ) : null}
          </div>

          {/* HUJJAT REKVIZITLARI — blankdan o'qish qiyin bo'lgan narsalar
              matn ko'rinishida: qidirish va nusxalash uchun. */}
          <dl className="flex flex-col gap-2 text-sm">
            <Fact label={t('accounting.colStatus')}>
              <Badge tone={survey.status === 'PENDING' ? 'warn' : 'success'}>
                {t(`status.survey.${survey.status}`)}
              </Badge>
            </Fact>
            <Fact label={t('accounting.colRequisition')}>
              <span className="tabular text-text">{survey.requisitionDocNo}</span>
            </Fact>
            <Fact label={t('accounting.colWarehouse')}>
              <span className="flex items-center gap-1.5 text-text">
                <Warehouse size={13} aria-hidden className="shrink-0 text-text-faint" />
                {warehouseName ?? `#${survey.warehouseId}`}
              </span>
            </Fact>
            {survey.throughPerson === null ? null : (
              <Fact label={t('requisition.fieldThroughPerson')}>
                <span className="text-text">{survey.throughPerson}</span>
              </Fact>
            )}
            {survey.purpose === null ? null : (
              <Fact label={t('requisition.fieldPurpose')}>
                <span className="text-pretty text-text">{survey.purpose}</span>
              </Fact>
            )}
            <Fact label={t('accounting.colCreatedAt')}>
              <span className="tabular text-text">{formatMoment(survey.createdAt)}</span>
            </Fact>
          </dl>

          {/* SATRLAR — blankdagi jadvalning o'qiladigan nusxasi. PDF ichida
              ular mayda va uzun ro'yxatda ikkinchi sahifaga o'tib ketadi. */}
          {detail.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <div className="overflow-hidden rounded-[var(--radius-md)] border border-border">
              <Table
                caption={t('accounting.reviewLines')}
                columns={columns}
                rows={detail.data?.lines ?? []}
                rowKey={(line) => line.id}
                density="compact"
                className="rounded-none border-0"
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

/** Bitta rekvizit — yorliq va qiymat, hamma joyda bir xil kenglikda. */
const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-baseline gap-3">
    <dt className="w-[9rem] shrink-0 text-xs text-text-faint">{label}</dt>
    <dd className="min-w-0 flex-1">{children}</dd>
  </div>
);
